import * as THREE from 'three';
import { COURT_LENGTH, COURT_WIDTH, PADDLE_WIDTH, PADDLE_Z, type Side, type WorldState } from '../game/world';

export class GameRenderer {
  readonly #renderer = new THREE.WebGLRenderer({ antialias: true });
  readonly #scene = new THREE.Scene();
  readonly #camera = new THREE.PerspectiveCamera(48, 1, 0.1, 100);
  readonly #ball: THREE.Mesh;
  readonly #paddle: THREE.Mesh;
  readonly #resizeObserver: ResizeObserver;
  #side: Side = 'south';

  constructor(private readonly host: HTMLElement) {
    this.#scene.background = new THREE.Color(0x030712);
    this.#scene.fog = new THREE.Fog(0x030712, 9, 19);

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(COURT_WIDTH, COURT_LENGTH),
      new THREE.MeshStandardMaterial({ color: 0x10243e, roughness: 0.9 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.z = 0;
    this.#scene.add(floor);

    const lineMaterial = new THREE.MeshBasicMaterial({ color: 0x38bdf8 });
    for (const x of [-COURT_WIDTH / 2, COURT_WIDTH / 2]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, COURT_LENGTH), lineMaterial);
      rail.position.set(x, 0.04, 0);
      this.#scene.add(rail);
    }
    const horizon = new THREE.Mesh(new THREE.BoxGeometry(COURT_WIDTH, 1.6, 0.18), new THREE.MeshStandardMaterial({ color: 0x020617 }));
    horizon.position.set(0, 0.8, 0);
    this.#scene.add(horizon);

    this.#paddle = new THREE.Mesh(new THREE.BoxGeometry(PADDLE_WIDTH, 0.7, 0.55), new THREE.MeshStandardMaterial({ color: 0x22d3ee, emissive: 0x083344 }));
    this.#ball = new THREE.Mesh(new THREE.SphereGeometry(0.28, 18, 12), new THREE.MeshStandardMaterial({ color: 0xf8fafc, emissive: 0x64748b }));
    this.#scene.add(this.#paddle, this.#ball, new THREE.HemisphereLight(0xffffff, 0x0f172a, 3));

    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    host.append(this.#renderer.domElement);
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(host);
    this.setSide('south');
    this.#resize();
  }

  setSide(side: Side): void {
    this.#side = side;
    const direction = side === 'south' ? 1 : -1;
    this.#camera.position.set(0, 10, direction * 13.5);
    this.#camera.lookAt(0, 0, direction * 3.5);
  }

  render(world: WorldState, localId: string): void {
    const local = world.paddles.get(localId);
    if (local) {
      this.setSide(local.side);
      this.#paddle.position.set(local.x, 0.38, local.side === 'south' ? PADDLE_Z : -PADDLE_Z);
      this.#paddle.visible = true;
    } else this.#paddle.visible = false;

    const inOwnHalf = this.#side === 'south' ? world.ball.z >= 0 : world.ball.z <= 0;
    this.#ball.visible = Boolean(local) && inOwnHalf;
    this.#ball.position.set(world.ball.x, 0.35, world.ball.z);
    this.#renderer.render(this.#scene, this.#camera);
  }

  dispose(): void {
    this.#resizeObserver.disconnect();
    this.#scene.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      object.geometry.dispose();
      for (const material of Array.isArray(object.material) ? object.material : [object.material]) material.dispose();
    });
    this.#renderer.dispose();
    this.#renderer.domElement.remove();
  }

  #resize(): void {
    const width = Math.max(this.host.clientWidth, 1);
    const height = Math.max(this.host.clientHeight, 1);
    this.#camera.aspect = width / height;
    this.#camera.updateProjectionMatrix();
    this.#renderer.setSize(width, height, false);
  }
}
